import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Droplets, Loader2, Power, PowerOff, Send } from "lucide-react";
import { Button } from "./ui/button";
import { usePopup } from "../context/PopupContext";
import { listDeviceCommands, sendDeviceCommand } from "../services/deviceService";
import { derivePumpUiState, formatCountdown, potLabelFromCommand } from "../utils/pumpActivityUtils";
import PumpCommandDialog from "./PumpCommandDialog";

function StatusBadge({ phase, badge }) {
  const styles = {
    sending: "bg-accent/15 text-accent border-accent/30",
    sent: "bg-primary/10 text-primary border-primary/25",
    running: "bg-success/15 text-success border-success/30",
    stopping: "bg-destructive/10 text-destructive border-destructive/25",
    failed: "bg-destructive/10 text-destructive border-destructive/25",
    idle: "bg-cream text-muted-foreground border-border",
  };
  const icons = {
    sending: <Loader2 className="h-3 w-3 animate-spin" />,
    sent: <Send className="h-3 w-3" />,
    running: <Power className="h-3 w-3" />,
    stopping: <PowerOff className="h-3 w-3" />,
    failed: <PowerOff className="h-3 w-3" />,
    idle: <PowerOff className="h-3 w-3" />,
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${styles[phase] || styles.idle}`}
    >
      {icons[phase] || icons.idle}
      {badge}
    </span>
  );
}

export default function PumpControl({
  deviceId,
  deviceLabel,
  online,
  canControl,
  disabledHint = "",
  liveReading = null,
  nodes = [],
}) {
  const { showPopup } = usePopup();
  const [commands, setCommands] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogAction, setDialogAction] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const submitLockRef = useRef(false);

  const refreshCommands = useCallback(async () => {
    if (!deviceId) return;
    try {
      const data = await listDeviceCommands(deviceId, { limit: 30 });
      setCommands(data?.items || []);
    } catch {
      // Non-blocking; card still usable for send.
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  const hubNodes = useMemo(() => (Array.isArray(nodes) ? nodes : []), [nodes]);
  const driestNodeId = useMemo(() => {
    const real = hubNodes.filter((n) => !String(n.nodeId || "").includes("_sim_"));
    const pool = real.length ? real : hubNodes;
    const withSoil = pool.filter((n) => Number.isFinite(Number(n.soil)));
    if (!withSoil.length) return pool[0]?.nodeId || "";
    return [...withSoil].sort((a, b) => Number(a.soil) - Number(b.soil))[0].nodeId;
  }, [hubNodes]);

  useEffect(() => {
    if (!selectedNodeId && driestNodeId) setSelectedNodeId(driestNodeId);
  }, [driestNodeId, selectedNodeId]);

  const uiState = useMemo(
    () => derivePumpUiState(commands, { localBusy: busy, nowMs }),
    [commands, busy, nowMs]
  );

  useEffect(() => {
    setLoading(true);
    refreshCommands();
    const inFlight = uiState.phase === "sent" || uiState.phase === "stopping" || uiState.phase === "running";
    const intervalMs = inFlight || busy ? 1000 : 4000;
    const timer = setInterval(refreshCommands, intervalMs);
    return () => clearInterval(timer);
  }, [refreshCommands, uiState.phase, busy]);

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const openDialog = (action) => {
    if (!canControl) {
      showPopup({
        type: "warning",
        title: "Pump control",
        message: disabledHint || "You cannot control this device.",
        ttlMs: 3500,
      });
      return;
    }
    if (uiState.locked && action === "on") {
      // Still allow — server queues behind current run
    } else if (uiState.phase === "stopping" || (uiState.phase === "sent" && uiState.latest?.action === "off")) {
      showPopup({
        type: "warning",
        title: "Pump busy",
        message: "OFF command is still processing. Please wait a few seconds.",
        ttlMs: 4000,
      });
      return;
    }
    if (action === "on" && !online) {
      showPopup({
        type: "warning",
        title: "Device offline",
        message: "Pump ON needs the device online. Check power and Wi-Fi, then try again.",
        ttlMs: 4000,
      });
      return;
    }
    setDialogAction(action);
    if (action === "on" && driestNodeId) setSelectedNodeId(driestNodeId);
  };

  const buildSensorAtStart = () => {
    if (!liveReading) return undefined;
    const snap = {};
    const temperature = liveReading.temperature ?? liveReading.t;
    const humidity = liveReading.humidity ?? liveReading.h;
    const soilMoisture = liveReading.soilMoisture ?? liveReading.s;
    if (Number.isFinite(Number(temperature))) snap.temperature = Number(temperature);
    if (Number.isFinite(Number(humidity))) snap.humidity = Number(humidity);
    if (Number.isFinite(Number(soilMoisture))) snap.soilMoisture = Number(soilMoisture);
    const values = Object.values(snap);
    if (!values.length) return undefined;
    if (values.every((v) => Number(v) === 0)) return undefined;
    return Object.keys(snap).length ? snap : undefined;
  };

  const runCommand = async (action, options = {}) => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setBusy(true);
    try {
      const durationSec = action === "on" ? Number(options.durationSec) || 120 : undefined;
      const nodeId = action === "on" ? options.nodeId || selectedNodeId : undefined;
      const potIndex = action === "on" ? options.potIndex : undefined;
      const result = await sendDeviceCommand(deviceId, {
        target: "pump",
        action,
        durationSec,
        nodeId,
        potIndex,
        sensorAtStart: action === "on" ? buildSensorAtStart() : undefined,
      });
      setCommands((prev) => [
        {
          commandId: result.commandId,
          action,
          durationSec: durationSec || 0,
          status: result.status || "pending",
          issuedAt: Date.now(),
          source: "app_manual",
          nodeId: nodeId || "",
          potIndex: potIndex || 0,
          queued: !!result.queued,
        },
        ...prev,
      ]);
      showPopup({
        type: "success",
        title: result.queued ? "Queued" : action === "on" ? "Pump ON sent" : "Pump OFF sent",
        message:
          result.queued
            ? result.message || "Will run after the current watering finishes."
            : action === "on"
              ? hubNodes.length
                ? "Command sent. Hub will open that pot valve, then start the pump."
                : "Command sent. Status will update when the device confirms."
              : "OFF sent. Pump will stop when the device picks it up.",
        ttlMs: 3500,
      });
      setDialogAction(null);
      await refreshCommands();
    } catch (error) {
      const msg =
        error?.status === 409
          ? error.message
          : error?.message || "Could not send pump command. Try again.";
      const soft = /already running|processing|in_flight|pump_running/i.test(String(msg));
      showPopup({
        type: soft ? "warning" : "error",
        title: soft ? "Almost ready" : "Command failed",
        message: soft
          ? "Previous watering is clearing automatically (~10s after it ends). Tap Water again shortly — no OFF needed."
          : msg,
        ttlMs: 5000,
      });
    } finally {
      setBusy(false);
      submitLockRef.current = false;
    }
  };

  const onDisabled = !canControl || !online || busy;
  const offDisabled = !canControl || busy;

  const queue = useMemo(
    () =>
      (commands || [])
        .filter((c) => String(c.action).toLowerCase() === "on" && String(c.status).toLowerCase() === "queued")
        .sort((a, b) => Number(a.issuedAt || 0) - Number(b.issuedAt || 0)),
    [commands]
  );
  const activePotLabel = uiState.activeCommand
    ? potLabelFromCommand(uiState.activeCommand, hubNodes)
    : "";
  const statusLine =
    activePotLabel && (uiState.phase === "running" || uiState.phase === "sent")
      ? `${activePotLabel} · ${uiState.summary}`
      : loading
        ? "…"
        : uiState.summary;

  return (
    <>
      <div
        className={[
          "rounded-2xl border p-4 transition-colors",
          uiState.running ? "border-success/40 bg-success/5" : "border-border bg-cream/30",
          uiState.phase === "sending" ? "border-accent/40 bg-accent/5" : "",
        ].join(" ")}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-muted-foreground">
            <Droplets className="h-4 w-4 text-accent" />
            Pump / relay
            {hubNodes.length ? <span className="normal-case tracking-normal"> · pick a pot</span> : null}
          </div>
          <div className="flex flex-col items-end gap-1 text-xs">
            <StatusBadge phase={uiState.phase} badge={uiState.badge} />
            {uiState.remainingSec != null && (uiState.phase === "running" || uiState.phase === "sent") ? (
              <span className="font-display text-lg tabular-nums text-success">
                {formatCountdown(uiState.remainingSec)}
              </span>
            ) : null}
            <span className="text-muted-foreground text-right max-w-[14rem]">{statusLine}</span>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={uiState.running || uiState.phase === "sent" ? "default" : "outline"}
            disabled={onDisabled}
            onClick={() => openDialog("on")}
          >
            Pump ON
          </Button>
          <Button size="sm" variant="outline" disabled={offDisabled} onClick={() => openDialog("off")}>
            Pump OFF
          </Button>
        </div>
        {!canControl && (
          <p className="mt-2 text-xs text-muted-foreground">Only the device owner can control the pump.</p>
        )}
        {canControl && !online && uiState.phase === "idle" && (
          <p className="mt-2 text-xs text-muted-foreground">
            Pump ON needs the device online. Pump OFF can still be queued when a run is active.
          </p>
        )}
        {activePotLabel && (uiState.phase === "running" || uiState.phase === "sent") && (
          <p className="mt-2 text-xs font-medium text-success">
            Pump open for {activePotLabel}
            {uiState.remainingSec != null ? ` · ${formatCountdown(uiState.remainingSec)} left` : ""}
          </p>
        )}
        {queue.length > 0 && (
          <p className="mt-2 text-xs text-foreground">
            Next:{" "}
            {queue.map((c, i) => `${i + 1}. ${potLabelFromCommand(c, hubNodes)}`).join(" → ")}
          </p>
        )}
        {uiState.locked && uiState.phase !== "sending" && (
          <p className="mt-2 text-xs text-muted-foreground">
            {uiState.phase === "running"
              ? "Motor is running for the pot above. Tap Water on another pot to queue it next."
              : "Please wait — another command is in progress."}
          </p>
        )}
      </div>

      <PumpCommandDialog
        open={Boolean(dialogAction)}
        action={dialogAction}
        deviceLabel={deviceLabel || deviceId}
        busy={busy}
        nodes={hubNodes}
        selectedNodeId={selectedNodeId}
        onSelectNode={setSelectedNodeId}
        onConfirm={(opts) => runCommand(dialogAction, opts)}
        onCancel={() => !busy && setDialogAction(null)}
      />
    </>
  );
}
